import { useState, useEffect } from 'react';
import {
  Building2,
  User,
  MapPin,
  Phone,
  ChevronRight,
  ArrowLeft,
  Calendar,
  Copy,
  Check,
} from 'lucide-react';
import { parseDisplayName, formatDate } from '@/lib/utils';
import type { Lead, Person } from '@/types/leads';
import styles from './DetailModal.module.css';

interface DetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: Lead | Person | null;
  type: 'place' | 'person';
  onNavigateToPlace?: (placeId: string) => void;
  onNavigateToPerson?: (personId: number) => void;
}

export const DetailModal = ({
  isOpen,
  onClose,
  item,
  type,
  onNavigateToPlace,
  onNavigateToPerson,
}: DetailModalProps) => {
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !item) return null;

  const isPlace = type === 'place';
  const place = isPlace ? (item as Lead) : (item as Person).place;
  const person = !isPlace ? (item as Person) : null;

  const email = !isPlace ? person?.email : null;
  const phoneNumber = !isPlace ? person?.phoneNumber : (place as Lead)?.internationalPhoneNumber;

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const getInitials = (name: string | null) => {
    if (!name) return '?';
    return name.slice(0, 1).toUpperCase();
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const displayName = isPlace
    ? parseDisplayName((place as Lead).displayName)?.text || 'Unnamed Place'
    : person?.fullName || 'No Name';

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          {!isPlace && person?.place && (
            <button
              onClick={() => onNavigateToPlace?.(person.place!.id)}
              className={styles.backButton}
            >
              <ArrowLeft className={styles.backIcon} />
              Back to business
            </button>
          )}

          <div className={styles.headerMain}>
            <div
              className={`${styles.avatarLarge} ${
                isPlace ? styles.avatarBlue : styles.avatarEmerald
              }`}
            >
              {isPlace ? (
                <Building2 className={styles.avatarLargeIcon} />
              ) : (
                <span>{getInitials(person?.fullName || null)}</span>
              )}
            </div>
            <div className={styles.headerInfo}>
              <div className={styles.badges}>
                <span className={styles.typeBadge}>{isPlace ? 'Business' : 'Contact'}</span>
                {isPlace && (place as Lead).rating && (
                  <span className={styles.ratingBadge}>{(place as Lead).rating} &#9733;</span>
                )}
              </div>
              <h2 className={styles.itemName}>{displayName}</h2>
              <div className={styles.metaRow}>
                <div className={styles.metaItem}>
                  <Calendar className={styles.metaIcon} />
                  <span>Added {formatDate(item.createdAt)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className={styles.content}>
          <div className={styles.infoGrid}>
            {/* Contact Info */}
            <div className={styles.infoCard}>
              <h3 className={styles.infoCardTitle}>
                <Phone className={styles.infoCardIcon} /> Contact Info
              </h3>
              <div className={styles.infoCardBody}>
                {email && (
                  <div className={styles.infoRow}>
                    <div className={styles.infoRowContent}>
                      <p className={styles.infoLabel}>Email</p>
                      <p className={styles.infoValue}>{email}</p>
                    </div>
                    <button
                      onClick={() => handleCopy(email, 'email')}
                      className={styles.copyButton}
                      title="Copy email"
                    >
                      {copied === 'email' ? (
                        <Check className={styles.copyIconSuccess} />
                      ) : (
                        <Copy className={styles.copyIcon} />
                      )}
                    </button>
                  </div>
                )}
                {phoneNumber && (
                  <div className={styles.infoRow}>
                    <div className={styles.infoRowContent}>
                      <p className={styles.infoLabel}>Phone</p>
                      <p className={`${styles.infoValue} ${styles.tabularNums}`}>{phoneNumber}</p>
                    </div>
                    <button
                      onClick={() => handleCopy(phoneNumber, 'phone')}
                      className={styles.copyButton}
                      title="Copy phone"
                    >
                      {copied === 'phone' ? (
                        <Check className={styles.copyIconSuccess} />
                      ) : (
                        <Copy className={styles.copyIcon} />
                      )}
                    </button>
                  </div>
                )}
                {!email && !phoneNumber && (
                  <div className={styles.noData}>No contact info available.</div>
                )}
              </div>
            </div>

            {/* Location / Business */}
            <div className={styles.infoCard}>
              <h3 className={styles.infoCardTitle}>
                {isPlace ? (
                  <MapPin className={styles.infoCardIcon} />
                ) : (
                  <Building2 className={styles.infoCardIcon} />
                )}
                {isPlace ? 'Location' : 'Business'}
              </h3>
              {isPlace && (place as Lead).formattedAddress ? (
                <div className={styles.infoCardBody}>
                  <p className={styles.addressText}>{(place as Lead).formattedAddress}</p>
                  <button
                    onClick={() => handleCopy((place as Lead).formattedAddress!, 'address')}
                    className={styles.copyAddressButton}
                  >
                    {copied === 'address' ? (
                      <Check className={styles.copyAddressIcon} />
                    ) : (
                      <Copy className={styles.copyAddressIcon} />
                    )}
                    {copied === 'address' ? 'Copied' : 'Copy address'}
                  </button>
                </div>
              ) : !isPlace && person?.place ? (
                <div className={styles.infoCardBody}>
                  <div>
                    <div className={styles.businessNameText}>
                      {parseDisplayName(person.place.displayName)?.text || 'Business'}
                    </div>
                    <p className={styles.businessAddress}>{person.place.formattedAddress}</p>
                  </div>
                  <button
                    onClick={() => onNavigateToPlace?.(person.place!.id)}
                    className={styles.viewBusinessButton}
                  >
                    View business
                    <ChevronRight className={styles.viewBusinessIcon} />
                  </button>
                </div>
              ) : (
                <div className={styles.noData}>No location info available.</div>
              )}
            </div>

            {/* Related Persons */}
            {isPlace && (
              <div className={`${styles.infoCard} ${styles.fullWidth}`}>
                <div className={styles.contactsHeader}>
                  <h3 className={styles.infoCardTitle}>
                    <User className={styles.infoCardIcon} /> Contacts
                  </h3>
                  <span className={styles.countBadge}>{(place as Lead).persons?.length || 0}</span>
                </div>
                <div className={styles.personsGrid}>
                  {(place as Lead).persons?.length > 0 ? (
                    (place as Lead).persons.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => onNavigateToPerson?.(p.id)}
                        className={styles.personCard}
                      >
                        <div className={styles.personCardContent}>
                          <div className={styles.personCardAvatar}>{getInitials(p.fullName)}</div>
                          <div className={styles.personCardInfo}>
                            <p className={styles.personCardName}>{p.fullName || 'Unknown'}</p>
                            <p className={styles.personCardEmail}>{p.email || 'No email'}</p>
                          </div>
                        </div>
                        <ChevronRight className={styles.personCardChevron} />
                      </button>
                    ))
                  ) : (
                    <div className={styles.noPersons}>No contacts found for this business.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button onClick={onClose} className={styles.closeButton}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
